import React, { useCallback, useEffect, useRef, useState } from 'react'
import { StreamMessage, useChannelStateContext, useChatContext } from 'stream-chat-react'
import { useChatShortcuts } from 'components/Chat/contexts/ChatShortcutsContext'
import { debounce } from 'lodash'
import './MessagingChannelHeader.css'
import type { StreamChatGenerics } from '../../types'
import { getSearchOperator } from '../../utils/searchHelpers'
import { isGroupChannel as checkIsGroupChannel } from '../../utils/channelHelpers'
import ArrowLeftIcon from 'components/Common/SvgIcons/ArrowLeftIcon'
import { isMobileOnly } from 'mobile-device-detect'
import { getChannelName, getInitials } from 'components/Chat/helper'
import ModalBox from 'components/Common/ModalBox'
import UserProfile from '../UserProfile'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import ChatBubbleIcon from 'assets/svgs/chat-bubble-icon.svg'
import FileIcon from 'assets/svgs/file-icon.svg'
import StarIcon from 'assets/svgs/star-icon.svg'
import { Attachment } from 'stream-chat'
import GroupMembersDisplay from '../GroupMembersDisplay/GroupMembersDisplay'
import GroupEdit from '../GroupEdit/GroupEdit'

type Props = {
  theme: string
  usersAssessmentResponseByQuestion: any
  setShowChannelPreview: (showChannelPreview) => void
  setShowDesktopUserProfile: (showChannelPreview) => void
  showDesktopUserProfile: boolean
  showChannelPreview: boolean
  activeTab?: string
  onTabChange?: (tab: string) => void
  onSearchResults?: (results: any[], query: string, isActive: boolean) => void
  searchQuery?: string
  setSearchQuery?: (query: string) => void
}

type UserName = {
  firstName: string
  lastName: string
}

const MessagingChannelHeader = (props: Props) => {
  const {
    theme,
    setShowChannelPreview,
    showChannelPreview,
    activeTab = 'messages',
    onTabChange,
    onSearchResults,
    searchQuery = '',
    setSearchQuery,
    // setShowDesktopUserProfile,
    // showDesktopUserProfile,
  } = props
  const { client } = useChatContext<StreamChatGenerics>()
  const { channel } = useChannelStateContext<StreamChatGenerics>()
  const [channelName, setChannelName] = useState(channel.data?.name || '')
  const [isEditing, setIsEditing] = useState(false)
  const [, setTitle] = useState('')
  const [showUserProfile, setShowUserProfile] = useState(false)
  const [showMessageSearch, setShowMessageSearch] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [showGroupEdit, setShowGroupEdit] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { registerOpenQuickSearch } = useChatShortcuts() ?? {}

  const members = Object.values(channel.state.members || {}).filter(
    (member) => member.user?.id !== client?.user?.id
  )

  const totalMembers = Object.keys(channel.state.members || {}).length
  const isGroupChannel = checkIsGroupChannel(channel)

  const handleOpenGroupEdit = () => {
    setShowGroupEdit(true)
  }

  const updateChannel = async () => {
    if (channelName && channelName !== channel.data?.name) {
      await channel.update(
        { name: channelName },
        { text: `Channel name changed to ${channelName}` }
      )
    }

    setIsEditing(false)
  }

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef?.current.focus()
    }
  }, [isEditing])

  useEffect(() => {
    if (!channelName) {
      setTitle(members.map((member) => member.user?.name).join(', '))
    }
  }, [channelName, members])

  useEffect(() => {
    if (!registerOpenQuickSearch) return
    registerOpenQuickSearch(() => {
      setShowMessageSearch(true)
      setTimeout(() => searchInputRef.current?.focus(), 100)
    })
    return () => registerOpenQuickSearch(null)
  }, [registerOpenQuickSearch])

  const EditHeader = () => (
    <form
      style={{ flex: 1 }}
      onSubmit={(event) => {
        event.preventDefault()
        inputRef?.current?.blur()
      }}
    >
      <input
        autoFocus
        className="channel-header__edit-input"
        onBlur={updateChannel}
        onChange={(event) => setChannelName(event.target.value)}
        placeholder="Type a new name for the chat"
        ref={inputRef}
        value={channelName}
      />
    </form>
  )

  const closemodal = () => {
    setShowChannelPreview(false)
  }

  // Search functionality
  const performSearch = useCallback(
    async (query: string) => {
      if (!query.trim() || !channel) return

      setIsSearching(true)

      try {
        if (activeTab === 'files') {
          // File-specific search - search only messages with attachments
          const channelFilters = {
            cid: channel.cid,
          }

          // First, get all messages with attachments
          const messageFilters = {
            attachments: { $exists: true }, // Only messages with attachments
          }

          const searchOptions = {
            limit: 100,
            sort: [{ created_at: -1 as const }], // Sort by creation date (newest first)
          }

          const response = await client.search(channelFilters, messageFilters, searchOptions)
          if (!searchQuery) {
            return
          }
          const fileResults: {
            message: StreamMessage<StreamChatGenerics>
            attachment: Attachment<StreamChatGenerics>
            matchType: string
          }[] = []
          const queryLower = query.toLowerCase()
          response.results?.forEach((searchResult) => {
            const message = searchResult.message
            if (message.attachments && message.attachments.length > 0) {
              message.attachments.forEach((attachment) => {
                // If no query, show all attachments
                if (!query.trim()) {
                  fileResults.push({
                    message: message,
                    attachment: attachment,
                    matchType: 'file',
                  })
                } else {
                  // Filter attachments based on query
                  const title = attachment.title?.toLowerCase() || ''
                  const fallback = attachment.fallback?.toLowerCase() || ''
                  const fileName = (attachment.file_name as string)?.toLowerCase() || ''
                  const assetUrl = attachment.asset_url?.toLowerCase() || ''

                  if (
                    title.includes(queryLower) ||
                    fallback.includes(queryLower) ||
                    fileName.includes(queryLower) ||
                    assetUrl.includes(queryLower)
                  ) {
                    fileResults.push({
                      message: message,
                      attachment: attachment,
                      matchType: 'file',
                    })
                  }
                }
              })
            }
          })

          onSearchResults?.(fileResults, query, true)
        } else {
          // Message search - search through messages and attachments

          // 1. Search messages (text based) using stream's search API
          // Use appropriate operator based on whether query contains special characters
          const messageFilterConditions = getSearchOperator(query)
          const messageSearchPromise = client.search(
            {
              type: 'messaging',
              cid: channel.cid,
            },
            messageFilterConditions,
            {
              sort: [{ created_at: -1 as const }],
              limit: 20,
            }
          )

          // 2. Query recent messages with attachments (for file name matching)
          // We fetch recent messages to find attachments that might match the query by name
          const attachmentQueryPromise = channel.query({
            messages: { limit: 100 },
          })

          const [messageResponse, channelState] = await Promise.all([
            messageSearchPromise,
            attachmentQueryPromise,
          ])

          // Process text search results
          const textResults = (messageResponse.results || []).map((result) => ({
            message: result.message,
            matchType: 'message',
            createdAt: new Date(result.message.created_at || 0).getTime(),
          }))

          // Process attachment results from local query
          const attachmentResults: any[] = []
          const queryLower = query.toLowerCase()

          // channelState.messages contains the recent messages
          const messagesToCheck = channelState.messages || []

          messagesToCheck.forEach((message) => {
            if (message.attachments && message.attachments.length > 0) {
              const hasAttachmentMatch = message.attachments.some((attachment) => {
                const title = (attachment.title || '').toLowerCase()
                const fallback = (attachment.fallback || '').toLowerCase()
                const fileName = ((attachment.file_name as string) || '').toLowerCase()
                return (
                  title.includes(queryLower) ||
                  fallback.includes(queryLower) ||
                  fileName.includes(queryLower)
                )
              })

              if (hasAttachmentMatch) {
                attachmentResults.push({
                  message: message as unknown as StreamMessage<StreamChatGenerics>,
                  matchType: 'message',
                  createdAt: new Date(message.created_at || 0).getTime(),
                })
              }
            }
          })

          // Combine results
          const allResults = [...textResults, ...attachmentResults]

          // Deduplicate by message ID
          const uniqueResults = Array.from(
            new Map(allResults.map((item) => [item.message.id, item])).values()
          )

          // Sort by date (newest first)
          uniqueResults.sort((a, b) => b.createdAt - a.createdAt)

          onSearchResults?.(uniqueResults, query, true)
        }
      } catch (error) {
        console.error('Search failed:', error)
      } finally {
        setIsSearching(false)
      }
    },
    [activeTab, channel, client, onSearchResults, searchQuery]
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery?.(value)
    performSearch(value)
  }

  const debouceSearchChange = debounce(handleSearchChange, 300)

  const clearSearch = () => {
    setSearchQuery?.('')
    onSearchResults?.([], '', false)
  }

  // Deactivate search when tab changes
  useEffect(() => {
    if (showMessageSearch) {
      clearSearch()
      setShowMessageSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  return (
    <>
      <div className="bg-app-white-100">
        {/* Header with user info */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-app-grey-25 h-[70px]">
          <div className="flex items-center gap-3">
            {isGroupChannel ? (
              <div className="w-10 h-10 bg-app-grey-30 rounded-full flex items-center justify-center overflow-hidden">
                {channel.data?.image ? (
                  <img
                    src={channel.data.image as string}
                    alt="Group icon"
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <svg
                    className="w-5 h-5 text-app-grey-70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                )}
              </div>
            ) : (
              <AppAvatar
                size="md"
                type="mixed"
                shape="Rounded"
                text={getInitials({
                  firstName: (members[0]?.user as unknown as UserName)?.firstName,
                  lastName: (members[0]?.user as unknown as UserName)?.lastName,
                })}
              />
            )}

            <div className="flex flex-col">
              {!isEditing ? (
                <>
                  <div className="flex items-center gap-2">
                    <h2
                      className={`text-lg font-semibold text-app-black-100 ${
                        isGroupChannel
                          ? 'cursor-pointer hover:text-app-primary-60 transition-colors'
                          : ''
                      }`}
                      onClick={isGroupChannel ? handleOpenGroupEdit : undefined}
                    >
                      {channel.data?.name ||
                        getChannelName(
                          Object.values(members).filter(({ user }) => user?.id !== client.userID)
                        )}
                    </h2>
                    {!isGroupChannel && (
                      <div className="w-2 h-2 bg-app-green-60 rounded-full"></div>
                    )}
                  </div>
                </>
              ) : (
                <EditHeader />
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Group Members Display */}
            {isGroupChannel && (
              <GroupMembersDisplay
                members={members}
                totalCount={totalMembers}
                onClick={handleOpenGroupEdit}
              />
            )}
            <button
              onClick={() => {
                setShowMessageSearch(!showMessageSearch)
                if (!showMessageSearch && searchInputRef.current) {
                  setTimeout(() => searchInputRef.current?.focus(), 100)
                }
              }}
              className={`p-2 rounded-lg transition-colors duration-150 ${
                showMessageSearch
                  ? 'bg-app-primary-20 text-app-primary-60'
                  : 'hover:bg-app-grey-10 text-app-grey-70'
              }`}
              title="Search messages"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation tabs */}
        <div className="px-3 py-3 border-none">
          <div className="flex space-x-8 border-b border-app-grey-25">
            <button
              onClick={() => {
                onTabChange?.('messages')
              }}
              className={`pb-3 border-b-2 font-medium text-sm transition-colors duration-150 flex items-center gap-2 ${
                activeTab === 'messages'
                  ? 'border-app-primary-60 text-app-primary-60'
                  : 'border-transparent text-app-grey-60 hover:text-app-black-100'
              }`}
            >
              <img
                src={ChatBubbleIcon}
                alt="messages"
                className="w-5 h-5 transition-all duration-150"
                style={{
                  filter:
                    activeTab === 'messages'
                      ? 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(246deg) brightness(104%) contrast(97%)'
                      : 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg)',
                }}
              />
              Messages
            </button>
            <button
              onClick={() => {
                onTabChange?.('files')
              }}
              className={`pb-3 border-b-2 font-medium text-sm transition-colors duration-150 flex items-center gap-2 ${
                activeTab === 'files'
                  ? 'border-app-primary-60 text-app-primary-60'
                  : 'border-transparent text-app-grey-60 hover:text-app-black-100'
              }`}
            >
              <img
                src={FileIcon}
                alt="files"
                className="w-5 h-5 transition-all duration-150"
                style={{
                  filter:
                    activeTab === 'files'
                      ? 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(246deg) brightness(104%) contrast(97%)'
                      : 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg)',
                }}
              />
              Files
            </button>
            <button
              onClick={() => {
                onTabChange?.('starred')
              }}
              className={`pb-3 border-b-2 font-medium text-sm transition-colors duration-150 flex items-center gap-2 ${
                activeTab === 'starred'
                  ? 'border-app-primary-60 text-app-primary-60'
                  : 'border-transparent text-app-grey-60 hover:text-app-black-100'
              }`}
            >
              <img
                src={StarIcon}
                alt="starred"
                className="w-5 h-5 transition-all duration-150"
                style={{
                  filter:
                    activeTab === 'starred'
                      ? 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(246deg) brightness(104%) contrast(97%)'
                      : 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg)',
                }}
              />
              Starred
            </button>
          </div>
        </div>

        {/* Inline Search Interface */}
        {showMessageSearch && (
          <div className="px-6 py-4 border-b border-app-grey-25 bg-app-grey-5">
            <div className="relative">
              <svg
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-app-grey-55"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder={
                  activeTab === 'files'
                    ? 'Search files...'
                    : 'Search messages, attachments, and more...'
                }
                // value={searchQuery}
                onChange={debouceSearchChange}
                className="w-full pl-12 pr-12 py-3 bg-app-white-100 border border-app-grey-25 rounded-lg text-base placeholder-app-grey-55 focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 hover:bg-app-grey-30 rounded-full transition-colors duration-150"
                >
                  <svg
                    className="w-4 h-4 text-app-grey-55"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
              {isSearching && !searchQuery && (
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-app-primary-60 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            <p className="text-sm text-app-grey-60 mt-2">
              {activeTab === 'files'
                ? 'Search through shared files'
                : 'Search through messages, attachments, and shared files'}
            </p>
          </div>
        )}
      </div>

      <div className="mdl:hidden mobile-header-only">
        <div className={`${theme} flex justify-between items-center xs:pt-3 md:pt-10 md:mt-7 px-3`}>
          <div onClick={() => setShowChannelPreview(!showChannelPreview)}>
            <ArrowLeftIcon className={'fill-silver'} />
          </div>
        </div>
        {showUserProfile && (
          <div className="xl:hidden">
            <ModalBox
              onClose={closemodal}
              width={`xs:w-full sm:!w-[420px] md:!!w-[420px] mdl:!w-[420px] `}
              title={isMobileOnly ? 'Files Received' : 'Files Received'}
            >
              <UserProfile
                usersAssessmentResponseByQuestion={props.usersAssessmentResponseByQuestion}
                setShowUserProfile={setShowUserProfile}
              />
            </ModalBox>
          </div>
        )}
      </div>

      {/* Group Edit Modal */}
      {showGroupEdit && <GroupEdit channel={channel} onClose={() => setShowGroupEdit(false)} />}
    </>
  )
}

export default MessagingChannelHeader
